// @flow
import React, { useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import AspectRatioContainer from './utils/AspectRatioContainer';
import Playlist from './Playlist';
import PlaylistUpdater, { getOverlaySettingsDraftPath, getOverlaySettingsPath } from './utils/PlaylistUpdater';
import VideoJS from './VideoJS';
import { AnalyticUtility, LiveStreamActionType } from '@ff/ui/utils/analyticUtils';
import useAnalytics from 'shared/hooks/useAnalytics';
import useSimpleReducer from 'shared/hooks/useSimpleReducer';
import { isWeChatBrowser } from 'shared/lib/useragentdetection';
import { getGenesisCDN } from 'shared/lib/clientSettings';
import isGenesis from 'shared/lib/isGenesis';

type Props = {
  region: string;
  video_src: string;
  title: string;
  subtitle: string;
  placeholderImageUrl: string;
  scheduledTime: {
    date: string;
    timezone: string;
  };
  videoAspectRatio: string;
  pipEnabled: boolean;
  options: {
    autoplay: boolean,
    muted: boolean,
    liveui: boolean
  };
  playlistSettings: {
    polling: {
      enabled: boolean;
      refreshInterval: string;
      path: string;
    },
    playlist: {
      source: string;
      title: string;
      subtitle: string;
      post: string;
    }[];
  };
};

// play the second video in playlist for picture in picture player
const PIP_INDEX = 1;
const YOUTUBE_COM = 'youtube.com';
const RETRY_INTERVAL_ON_ERROR = 5000;
// ISSUE: switching between youtube single video and youtube playlist may make the playlist icon in iframe disappear

const isWeChat = isWeChatBrowser();

export default function LivestreamVideoPlayer(props: Props) {
  const {
    region,
    title,
    subtitle,
    placeholderImageUrl,
    scheduledTime,
    pipEnabled,
    hideMobilePlaylist,
    videoAspectRatio,
    options: {
      autoplay,
      muted,
      liveui,
    } = {
      autoplay: true,
      muted: true
    },
    playlistSettings,
    howToShareVideo: {
      sectionTag
    },
  } = props;

  const { playlist: playlistFromProps } = playlistSettings || {};

  const c: { [key: string]: string } = useStyles(props);

  const playerRef = useRef(null);
  const pipRef = useRef(null);
  const errorRetryTimeoutRef = useRef(null);
  const pipPausedTime = useRef(null);
  const containerRef = useRef(null);
  const analytics: AnalyticUtility = useAnalytics();

  const [state, setState] = useSimpleReducer({
    // initial state
    overlayEnabled: true,
    pipActive: pipEnabled,
    playlist: playlistSettings?.polling?.enabled ? [] : playlistFromProps,
    currentPlayingIndex: 0
  });

  const { overlayEnabled, pipActive, playlist, currentPlayingIndex } = state;
  const mappedPlaylist = mapPlaylistWithType(playlist);

  const currentPlayingItem = playlist[currentPlayingIndex];
  const isYoutube = currentPlayingItem?.source?.includes(YOUTUBE_COM);

  const shareParam = new URLSearchParams(window.location.search).get('share');
  // Remove trailing slash from the video param
  const cleanedVideoParam = shareParam?.endsWith('/') ? shareParam.slice(0, -1) : shareParam;

  /**
   * Play a certain video in the playlist if the URL contains the "share" param.
   * Example: https://www.ff.com/us/91launch/?share=Youtube Share Playlist should play the Youtbe Share Playlist video.
   * In the props-schema file, view the howToShareVideo for more instructions.
   */
  useEffect(() => {
    let timer;
    // Effect for handling scroll and video index
    const scrollToSection = () => {
      // Check if the URL hash matches the section tag
      if (window.location.hash === `#${sectionTag}`) {
        // Scroll the container into view after a short delay
        timer = setTimeout(() => {
          containerRef.current?.scrollIntoView();
        }, 250);
      }
    }

    const updateCurrentPlayingIndex = () => {
      // Check if video parameter and playlist are provided
      if (cleanedVideoParam && playlistFromProps) {
        // Find the index of the video with the matching title
        const currentPlayingIndex = playlistFromProps.findIndex((video) => video.shareId === cleanedVideoParam);

        // If the video is found, update the state and close the Picture-in-Picture mode
        if (currentPlayingIndex !== -1) {
          setState({ pipActive: false, currentPlayingIndex: currentPlayingIndex });
        }
      }
    }

    scrollToSection();
    updateCurrentPlayingIndex();

    // Cleanup function to clear any active timeouts
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // need to update settings when overlayEnabled changes
    // options for videojs are only for initialization
    if (playerRef.current) {

      if (overlayEnabled) {
        // hide and mute when overlay appears
        playerRef.current.controls(false);
        playerRef.current.muted(true);
      } else {
        // show controls again when overlay disappears
        playerRef.current.controls(true);
      }
    }
  }, [overlayEnabled]);

  // fetch overlay settings from s3
  useFetchOverlaySettings({ playlistSettings, region, setState });

  const options = {
    autoplay,
    // mute video when overlay is active
    muted: overlayEnabled ? true : muted,
    loadingSpinner: true,
    liveui,
    // hide controls when overlay is active
    controls: !overlayEnabled,
    responsive: true,
    fluid: false,
    // display big play button is autoplay is off or for WeChat browser
    bigPlayButton: isWeChat || !autoplay,
    // no error message on video player when video fails to start
    errorDisplay: false,
    sources: mappedPlaylist?.[currentPlayingIndex]?.sources,
    // youtube settings
    youtube: {
      loop: autoplay ? 1 : 0 // loop videos
    }
  };

  const handlePlaylistUpdate = (newPlaylist) => {

    // first time loaded playlist
    if (playlist.length === 0) {
      setState({ currentPlayingIndex: 0, playlist: newPlaylist });
    } else {
      // look for new playing index in new playlist
      const newPlayingIndex = newPlaylist.findIndex(x => x.source === currentPlayingItem?.source);
      // index is -1 when not found (video is remove from playlist),
      // currentPlayingItem will become undefined next render, currently playing video won't be affected
      // if autoplay is on, index will be increased by 1 for next video, -1 + 1 = 0, which is the first video in playlist
      setState({ currentPlayingIndex: newPlayingIndex, playlist: newPlaylist });
    }
  };

  const shouldShowOverlay = () => {
    const timeout = new Date(scheduledTime?.date).getTime() - Date.now();
    if (timeout > 0) {
      // remove overlay when scheduled time is up
      setTimeout(() => {
        setState({ overlayEnabled: false });
      }, timeout);
    } else {
      setState({ overlayEnabled: false });
    }
  }

  // NOTE: Be Aware of Stale Closures in player events
  const mainPlayerReady = (player) => {
    playerRef.current = player;

    shouldShowOverlay();

    let lastWatchedSeconds = undefined;

    // if is playing, remove overlay incase timeout is not done yet
    player.on('playing', () => {
      analytics.trackLivestreamEvent(LiveStreamActionType.PLAY, player?.currentSrc());

      // resume pip at paused time
      if (pipPausedTime.current) {
        playerRef.current.currentTime(pipPausedTime.current);
        pipPausedTime.current = null;
      }
    });

    player.on('error', () => {
      analytics.trackLivestreamError(
        player?.currentSrc(),
        player?.error()?.message || 'unknown error'
      );

      // retry video source on error
      errorRetryTimeoutRef.current = setTimeout(() => {
        player.src(player.currentSrc());
      }, RETRY_INTERVAL_ON_ERROR);

    });

    player.on('timeupdate', () => {
      const currentWatchSeconds = Math.floor(player?.currentTime());

      // fires watchtime event every 10 seconds.
      if (currentWatchSeconds > 0 && currentWatchSeconds % 10 === 0 && lastWatchedSeconds !== currentWatchSeconds) {
        lastWatchedSeconds = currentWatchSeconds;

        analytics.trackLivestreamEvent(LiveStreamActionType.TIMEUPDATE, player?.currentSrc(), currentWatchSeconds);
      }
    });

    player.on('pause', () => {
      analytics.trackLivestreamEvent(LiveStreamActionType.PAUSE, player?.currentSrc(), getPlayDuration(player?.duration()));
    });

    player.on('ended', () => {
      analytics.trackLivestreamEvent(LiveStreamActionType.ENDED, player?.currentSrc(), getPlayDuration(player?.duration()));

      // if autoplay is on, play next video when current video ends
      if (options.autoplay) {
        setState(prevState => ({ currentPlayingIndex: prevState.currentPlayingIndex + 1 }));
      }
    });
  } // end of mainPlayerReady

  const handlePIPClose = () => {
    setState({ pipActive: false });
  }

  const pipReady = (pipPlayer) => {
    pipRef.current = pipPlayer;

    // remove pip player when video has reached the end
    pipPlayer.on('ended', () => {
      handlePIPClose();
    });
  }

  const handlePlaylistItemSelect = (index) => {
    handlePIPClose();
    setState({ currentPlayingIndex: index });

    if (errorRetryTimeoutRef.current) {
      clearTimeout(errorRetryTimeoutRef.current);
    }

    // apply overlay only on first video, when first video is selected on playlist
    if (index === 0) {
      setState({ overlayEnabled: true });
      shouldShowOverlay();
    } else {
      setState({ overlayEnabled: false })
    }
  }

  const handlePIPClick = (e) => {
    if (pipRef.current?.options_.bigPlayButton && pipRef.current?.controls_) {
      // 'controls' is only true for WeChat, when autoplay is not working.
      // Disable 'controls' and start PIP playback. Next click will make PIP the main video.
      pipRef.current.controls(false);
      return e.preventDefault();
    }
    handlePIPClose();
    // save current paused time and resume later at main player
    pipPausedTime.current = pipRef.current?.currentTime();
    setState({ currentPlayingIndex: PIP_INDEX });
  }

  return (
    <div id={sectionTag} ref={containerRef} className={c.container}>
      {playlistSettings?.polling?.enabled &&
        <PlaylistUpdater
          region={region}
          path={playlistSettings.polling.path}
          refreshInterval={playlistSettings.polling.refreshInterval}
          onUpdate={handlePlaylistUpdate}
        />
      }
      <AspectRatioContainer
        // TODO: may not work for other aspect ratio
        flex={3}
        ratio={videoAspectRatio}
      >
        {/* regular player */}
        {
          <VideoJS
            options={options}
            onReady={mainPlayerReady}
            overlay={overlayEnabled}
            overlayOptions={{
              title,
              subtitle,
              placeholderImageUrl,
            }}
            floatingInfo={{
              title: currentPlayingItem?.title,
              subtitle: currentPlayingItem?.subtitle
            }}
          />
        }
        {/* Picture in Picture player */}
        {
          pipActive &&
          !isYoutube && // youtube doesn't work well with pip
          mappedPlaylist[PIP_INDEX]?.sources &&
          <div className={c.pictureInPictureContainer}>
            <VideoJS
              onClick={handlePIPClick}
              onClose={handlePIPClose}
              onReady={pipReady}
              options={{
                // Android WeChat - autoplay does not work. Controls is required to show Play button
                controls: isWeChat,
                bigPlayButton: isWeChat,
                autoplay: true,
                muted: true,
                errorDisplay: false,
                sources: mappedPlaylist[PIP_INDEX].sources
              }}
            />
          </div>
        }
      </AspectRatioContainer>

      {playlist?.length > 1 &&
        <Playlist
          hideMobilePlaylist={hideMobilePlaylist}
          list={playlist}
          onSelect={handlePlaylistItemSelect}
          currentPlayingItem={currentPlayingIndex}
          region={region}
        />
      }
    </div>
  );
}

function useFetchOverlaySettings({ playlistSettings, region, setState }) {
  useEffect(() => {
    const downloadOverlaySettings = async () => {
      const playlistPath = isGenesis ? getOverlaySettingsDraftPath(playlistSettings.polling.path) : getOverlaySettingsPath(playlistSettings.polling.path);

      try {
        const res = await fetch(`${getGenesisCDN(region)}/${playlistPath}?v=${Date.now()}`);
        const content = await res.json()

        if (content?.data) {
          // change overlay settings based on s3 json file
          setState({ overlayEnabled: content.data.enabled });
        }
      } catch (error) {
        console.error('Failed to fetch overlay settings', error);
      }
    }
    // ---------------start polling--------------------------
    let intervalId;
    // first retrieve
    downloadOverlaySettings();

    if (playlistSettings.polling.refreshInterval > 0) {
      intervalId = setInterval(() => {
        downloadOverlaySettings();
      }, playlistSettings.polling.refreshInterval * 1000);
    }

    return () => clearInterval(intervalId);
  }, [playlistSettings.polling.path, playlistSettings.polling.refreshInterval, region, setState]);
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',

    '@media(max-width: 960px)': {
      display: 'block',
      height: 'auto',
    },

    // data-vjs-player style
    '& .video-js': {
      width: '100%',
      height: '100%'
    }
  },
  pictureInPictureContainer: {
    width: '30%',
    height: '30%',
    position: 'absolute',
    bottom: 30,
    right: 10,
    '&:hover': {
      transform: 'scale(1.02)',
      filter: 'brightness(130%)'
    }
  },
});

function mapPlaylistWithType(pList) {
  return pList.map((item) => {
    let type;

    if (item?.source?.endsWith('.mp4')) {
      type = 'video/mp4';
    } else if (item?.source?.endsWith('.m3u8')) {
      type = 'application/x-mpegURL';
    }
    else if (item?.source?.includes(YOUTUBE_COM)) {
      type = 'video/youtube';
    }

    return {
      ...item,
      sources: {
        src: item?.source,
        type,
      }
    }
  });
}

function getPlayDuration(duration) {
  //avoid value 'Infinity' returns from livestream source
  if (!duration || duration === Infinity) {
    return undefined;
  }

  return duration;
}
